import { type MutationProduct } from '@shared/types'
import { type ProductCommerceData } from '@shipt/cdui-modules/product-shelf/schema'
import { type ProductForEvent } from '@/features/shared/analytics/products'
import { type Beacons } from '@/features/shop/services/Search/types'
import { type ProductCampaignsPromotion } from '@/features/shop/services/Product/types'
import { type ProductInstructionsModalProduct } from '@/features/shop/components/Modal/ProductInstructionsModal'
import { type VariationsQuickAddModalProduct } from '@/features/shop/components/Modal/VariationsQuickAddModal'
import { type SubstituteProduct } from '@/features/shop/services/Substitutions/types'

type CduiCommerceGateProduct = {
  id: number
  universal_id: string
  storeId: number
}

const toId = (value: string | undefined): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toParentId = (commerce: ProductCommerceData): number | null =>
  commerce.parentProductId ? toId(commerce.parentProductId) : null

/**
 * Cart mutations and the variation-modal overlay require Segway's full
 * `ProductCampaignsPromotion`. CDUI only ships `categoryId`; the rest is unused
 * padding so the object type-checks. Downstream only reads `category_id`.
 */
const toCpgPromotion = (commerce: ProductCommerceData): ProductCampaignsPromotion | null => {
  const categoryId = commerce.cpgPromotion?.categoryId
  if (categoryId === undefined) return null
  return {
    category_id: categoryId,
    category_name: '',
    display_title: '',
    id: 0,
    start_date: '',
  }
}

export const toCommerceGateProduct = (commerce: ProductCommerceData): CduiCommerceGateProduct => ({
  id: toId(commerce.id),
  universal_id: commerce.universalId ?? '',
  storeId: commerce.storeId ?? 0,
})

/** Cart/order mutation shape from #17291. Live quantity/note are filled at call time. */
export const toMutationProduct = (commerce: ProductCommerceData): MutationProduct => ({
  id: toId(commerce.id),
  name: commerce.name,
  note: '',
  type: commerce.productType ?? '',
  storeId: commerce.storeId ?? 0,
  couponId: commerce.couponId ?? '',
  quantity: 0,
  unitWeight: commerce.unitWeight ?? 0,
  description: '',
  cpgPromotion: toCpgPromotion(commerce),
  nmu_eligible: commerce.nmuEligible ?? false,
  hasCustomLabel: commerce.hasCustomLabel ?? false,
  rootCategoryId: commerce.rootCategoryId ?? 0,
  isCustomProduct: commerce.isCustomProduct ?? false,
  parentProductId: toParentId(commerce),
  hierarchical_categories: commerce.hierarchicalCategories ?? {},
})

const toBeacons = (beacons: ProductCommerceData['beacons']): Beacons | undefined => {
  if (!beacons) return undefined
  return {
    onViewBeacon: beacons.onView,
    onClickBeacon: beacons.onClick,
    onBasketChangeBeacon: beacons.onBasketChange,
  }
}

export const toAnalyticsProduct = (commerce: ProductCommerceData): ProductForEvent => {
  const onSale = commerce.pricing?.onSale ?? false
  const categoryId = commerce.cpgPromotion?.categoryId
  return {
    id: toId(commerce.id),
    name: commerce.name,
    brandName: commerce.brandName ?? '',
    price: commerce.pricing?.unitPrice ?? 0,
    universal_id: commerce.universalId ?? '',
    onSale,
    salePrice: commerce.pricing?.saleUnitPrice ?? 0,
    parentProductId: toParentId(commerce),
    couponId: commerce.couponId ?? '',
    cpgPromotion: categoryId === undefined ? null : { category_id: categoryId },
    featured: commerce.featured ?? false,
    featuredTransactionId: commerce.featuredTransactionId,
    image: { url: commerce.imageUrl ?? '' },
    beacons: toBeacons(commerce.beacons),
    bogo: commerce.bogo ?? false,
    bought: commerce.bought ?? false,
    oos: commerce.oos ?? false,
    lowStock: commerce.lowStock ?? false,
  }
}

export const toSubstituteProduct = (commerce: ProductCommerceData): SubstituteProduct => ({
  id: toId(commerce.id),
  name: commerce.name,
  brandName: commerce.brandName ?? '',
  price: commerce.pricing?.unitPrice ?? 0,
  onSale: commerce.pricing?.onSale ?? false,
  salePrice: commerce.pricing?.saleUnitPrice ?? 0,
  parentProductId: toParentId(commerce),
  universal_id: commerce.universalId ?? '',
})

export const toInstructionsModalProduct = (
  commerce: ProductCommerceData,
  extras?: { note?: string },
): ProductInstructionsModalProduct => ({
  ...toSubstituteProduct(commerce),
  note: extras?.note ?? '',
  rootCategoryId: commerce.rootCategoryId ?? 0,
  isCustomProduct: commerce.isCustomProduct ?? false,
  storeId: commerce.storeId ?? 0,
})

export const toVariationModalProduct = (
  commerce: ProductCommerceData,
): VariationsQuickAddModalProduct => ({
  id: toId(commerce.id),
  // The modal fetches children by `parentProductId`. CDUI's `variationGroupId`
  // is the picker signal; `parentProductId` is PDP-only and must not open it.
  parentProductId: commerce.variationGroupId ? toId(commerce.variationGroupId) : null,
  couponId: commerce.couponId ?? '',
  cpgPromotion: toCpgPromotion(commerce),
})
